import { Component, DestroyRef, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { PaceBarcode } from '@evorait/pace-components';
import { BarcodeScannerService } from '../../services/barcode-scanner/barcode-scanner.service';
import { Observable, Subject, switchMap } from 'rxjs';
import { TextModel } from '../../models/primitive/text.model';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { TextModelOptions } from '../../models/options/text-model-options';
import { v4 as uuidv4 } from 'uuid';
import { finalize, mergeMap, take } from 'rxjs/operators';
import { ConnectivityService } from '../../services/connectivity/connectivity.service';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-core-barcode-clipboard-modal',
    templateUrl: './barcode-clipboard-modal.component.html',
    styleUrls: ['./barcode-clipboard-modal.component.scss'],
})
export class BarcodeClipboardModalComponent implements OnInit {
    public isOnline$ = this.connectivityService.isOnline$;
    public barcodes$: Observable<PaceBarcode[]> = this.barcodeScannerService.barcodeClipboard$;
    public barcodeTextModel: TextModel = new TextModel(
        'barcodeScanDummy',
        '',
        new TextModelOptions({ required: true, scannable: true }),
    );
    public labelTextModel: TextModel = new TextModel(
        'barcodeScanDummy',
        '',
        new TextModelOptions({ required: true, scannable: false }),
    );

    public addBarcodeForm: FormGroup;
    public formChildren: FormArray;

    protected strings: {
        downloadButtonLabel: string;
        uploadButtonLabel: string;
    };

    public barcodeUpdates = new Subject<PaceBarcode[]>();

    public constructor(
        private readonly modalInstance: BsModalRef,
        private readonly barcodeScannerService: BarcodeScannerService,
        private readonly connectivityService: ConnectivityService,
        private readonly formBuilder: FormBuilder,
        private readonly translate: TranslateService,
        private readonly destroyRef: DestroyRef,
    ) {}

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.addBarcodeForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });
        this.strings = {
            downloadButtonLabel: <string>this.translate.instant('BARCODE_CLIPBOARD_LABEL_BUTTON_FETCH_CLIPBOARD'),
            uploadButtonLabel: <string>this.translate.instant('BARCODE_CLIPBOARD_LABEL_BUTTON_UPLOAD_CLIPBOARD'),
        };

        this.barcodeScannerService.fetchBarcodeClipboard().pipe(take(1)).subscribe();

        this.barcodeUpdates
            .asObservable()
            .pipe(
                switchMap((barcodes) => this.updateBarcodes$(barcodes)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    public hideModal(): void {
        this.uploadBarcodes$()
            .pipe(finalize(() => this.modalInstance.hide()))
            .subscribe();
    }

    private updateBarcodes$(barcodes: PaceBarcode[]): Observable<boolean> {
        return this.barcodeScannerService.setBarcodeClipboard(barcodes).pipe(take(1));
    }

    public addBarcode(): void {
        const barcode: PaceBarcode = {
            barcode: this.barcodeTextModel.value,
            label: this.labelTextModel.value,
            timestamp: new Date().toISOString(),
            id: uuidv4(),
        };
        this.barcodeScannerService.barcodeClipboard$
            .pipe(
                take(1),
                mergeMap((barcodes) => this.updateBarcodes$([barcode, ...barcodes])),
            )
            .subscribe(() => {
                this.barcodeTextModel.value = '';
                this.labelTextModel.value = '';
                this.formChildren.reset([]);
                this.addBarcodeForm.reset();
            });
    }

    public uploadBarcodes$(): Observable<boolean> {
        return this.barcodeScannerService.barcodeClipboard$.pipe(
            switchMap((barcodes) => this.barcodeScannerService.uploadBarcodeClipboard(barcodes)),
            take(1),
        );
    }
}
